import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  TextField,
  useTheme,
} from "@mui/material";
import { MoreVert, Clear } from "@mui/icons-material";
import { getLabelWithRemainingChars } from "../../Helpers";
import { UserAutocomplete } from "../User/Autocomplete";
import { UserList } from "../User/List";
import { Team as TeamType, User } from "../../types";
import { useMutation } from "@apollo/client";
import { ADD_GROUP } from "../../Queries";
import { useErrorBoundary } from "../../Contexts/ErrorContext";
import { t } from "i18next";
import { LoadingButton } from "@mui/lab";

export interface TeamNewProps {
  users: User[] | null;
  onClose: () => void;
}

function EmptyState() {
  return <Box></Box>;
}

export const TeamNew = ({ users, onClose }: TeamNewProps) => {
  const { setError } = useErrorBoundary();
  const theme = useTheme();

  //
  // State
  //

  const [menuAnchor, setMenuAnchor] = useState<[HTMLElement, User] | null>(
    null,
  );

  const [team, updateTeam] = useState<TeamType>({
    id: "",
    name: "",
    userIds: [],
  });

  //
  // Mutations
  //

  const [addGroup, { loading: isCreating }] = useMutation(ADD_GROUP, {
    refetchQueries: ["GetGroups"],
    onCompleted: () => {
      reset();
      onClose();
    },
    onError: (error) => setError(t("errors.groups.create"), error),
  });

  //
  // Event handlers
  //

  function handleMenuClose() {
    setMenuAnchor(null);
  }

  function handleMenuClick(user: User, e: React.MouseEvent<HTMLElement>) {
    setMenuAnchor([e.currentTarget, user]);
  }

  function handleNameChange(name: string) {
    updateTeam({ ...team, name });
  }

  function handleAddUser(userId: string) {
    updateTeam({ ...team, userIds: [...team.userIds, userId] });
  }

  function handleRemoveUser(user: User) {
    updateTeam({
      ...team,
      userIds: team.userIds.filter((id) => id !== user.id),
    });
  }

  function handleCreate() {
    if (team.name.trim().length === 0) {
      setError("Please enter a team name", null);
      return;
    }
    addGroup({
      variables: {
        name: team.name,
        userIds: team.userIds,
      },
    });
  }

  function handleCancel() {
    reset();
    onClose();
  }

  //
  // Helpers
  //

  function reset() {
    updateTeam({
      id: "",
      name: "New team",
      userIds: [],
    });
  }

  const teamUsers = users
    ? users.filter((user) => team.userIds.indexOf(user.id) >= 0)
    : [];
  const menuOpen = Boolean(menuAnchor);

  return (
    <Card
      sx={{
        mb: 2,
        border: `5px solid ${theme.palette.primary.main}`,
      }}
    >
      <CardHeader
        title={
          <TextField
            variant="outlined"
            label={getLabelWithRemainingChars(
              "Team Name",
              team.name.length,
              255,
            )}
            size="small"
            value={team.name}
            fullWidth={true}
            placeholder="Enter team name"
            onBlur={(e) => handleNameChange(e.target.value.trim())}
            onChange={(e) => handleNameChange(e.target.value)}
            inputProps={{ maxLength: 255 }}
          />
        }
        action={
          <Box display="flex" gap={1} sx={{ m: 1 }}>
            <Button
              variant="text"
              aria-label="cancel create team"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <LoadingButton
              variant="contained"
              aria-label="save and create new team"
              onClick={handleCreate}
              loading={isCreating}
            >
              Save
            </LoadingButton>
          </Box>
        }
      />
      <CardContent>
        {!teamUsers.length && <EmptyState />}
        <Box sx={{ textAlign: "center", mb: 2 }}>
          <UserAutocomplete
            label="Assign Team Members"
            onChange={(user) => handleAddUser(user.id)}
            showAvatars={true}
            users={
              users
                ? users.filter((target) => team.userIds.indexOf(target.id) < 0)
                : []
            }
          />
        </Box>
        <UserList
          users={teamUsers}
          leader={team.leaderId}
          UserActions={({ user }) => (
            <IconButton
              onClick={(e) => handleMenuClick(user, e)}
              size="small"
              sx={{ ml: 2 }}
              aria-controls={menuOpen ? "user-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={menuOpen ? "true" : undefined}
            >
              <MoreVert />
            </IconButton>
          )}
        />
      </CardContent>
      <Menu
        anchorEl={menuAnchor && menuAnchor[0]}
        id="account-menu"
        open={menuOpen}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: "visible",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
            mt: 1.5,
            "& .MuiAvatar-root": {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            "&:before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: "background.paper",
              transform: "translateY(-50%) rotate(45deg)",
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {/* <MenuItem
          onClick={() => menuAnchor && handleAssignLeader(menuAnchor[1])}
        >
          <ListItemIcon>
            <Star fontSize="small" />
          </ListItemIcon>
          Assign student as team lead
        </MenuItem>
        <Divider /> */}
        <MenuItem onClick={() => menuAnchor && handleRemoveUser(menuAnchor[1])}>
          <ListItemIcon>
            <Clear fontSize="small" />
          </ListItemIcon>
          Remove student from team
        </MenuItem>
      </Menu>
    </Card>
  );
};
